/**
 * Run lifecycle for the agentic screening page: trigger, follow, settle.
 *
 * Counters come off the event stream rather than from polling — `started` carries
 * the item count and `session_finished` carries that item's turns and cost — so the
 * numbers on screen stay truthful mid-run without asking the server anything. When
 * the stream ends the run record is fetched once, which reconciles the totals and
 * pulls in the result rows.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import type {
  ScreeningLogEvent,
  ScreeningRunResults,
  ScreeningRunStatus,
} from "../api/clinicalTrialsApiTypes";

/** Live totals derived from the event stream. */
export interface RunCounters {
  items: number;
  done: number;
  turns: number;
  cost: number;
}

const EMPTY: RunCounters = { items: 0, done: 0, turns: 0, cost: 0 };

/**
 * Follow one screening run.
 *
 * @returns Run id, state, counters, log events, results and the two controls
 */
export function useScreeningRun() {
  const [runId, setRunId] = useState("");
  const [state, setState] = useState("");
  const [events, setEvents] = useState<ScreeningLogEvent[]>([]);
  const [counters, setCounters] = useState<RunCounters>(EMPTY);
  const [status, setStatus] = useState<ScreeningRunStatus | null>(null);
  const [results, setResults] = useState<ScreeningRunResults | null>(null);
  const [startedAt, setStartedAt] = useState(0);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const abort = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    setStreaming(false);
  }, []);

  useEffect(() => stop, [stop]);

  const consume = useCallback((event: ScreeningLogEvent) => {
    setEvents((prior) => [...prior, event]);
    setCounters((prior) => {
      if (event.event === "started") {
        return { ...prior, items: event.items ?? prior.items };
      }
      if (event.event === "session_finished") {
        return {
          items: prior.items,
          done: prior.done + 1,
          turns: prior.turns + (event.turns ?? 0),
          cost: prior.cost + (event.cost_usd ?? 0),
        };
      }
      return prior;
    });
    if (event.event === "status" && event.status) setState(event.status);
  }, []);

  /** Reconcile against the server once the stream has ended. */
  const settle = useCallback(async (id: string) => {
    try {
      const record = await clinicalTrialsService.getScreeningRun(id);
      setStatus(record);
      setState(record.status);
      setCounters({
        items: record.items_total,
        done: record.items_done,
        turns: record.turns ?? 0,
        cost: record.cost_usd ?? 0,
      });
      const rows = await clinicalTrialsService.getScreeningResults(id);
      if (rows.rows.length) setResults(rows);
    } catch {
      // A run that finished before its results were written simply has none yet.
    }
  }, []);

  /**
   * Attach to a run's log stream from its start.
   *
   * @param id - Run identifier
   */
  const follow = useCallback(
    (id: string) => {
      stop();
      const controller = new AbortController();
      abort.current = controller;
      setRunId(id);
      setEvents([]);
      setResults(null);
      setStatus(null);
      setCounters(EMPTY);
      setState("running");
      setStartedAt(Date.now());
      setStreaming(true);
      setError("");
      void clinicalTrialsService
        .streamScreeningLogs(id, consume, controller.signal)
        .catch((err: Error) => {
          if (!controller.signal.aborted) setError(err.message);
        })
        .finally(() => {
          if (controller.signal.aborted) return;
          setStreaming(false);
          void settle(id);
        });
    },
    [consume, settle, stop],
  );

  return {
    runId,
    state,
    events,
    counters,
    status,
    results,
    startedAt,
    streaming,
    error,
    follow,
    stop,
  };
}
